import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { OpenDialogAdminAnnounceComponent } from 'app/components/dialog/open-dialog-admin-announce/open-dialog-admin-announce.component';
import { OpenDialogDeleteElementComponent } from 'app/components/dialog/open-dialog-delete-element/open-dialog-delete-element.component';
import { Notice } from 'app/shared/models/class/notice/notice';
import { FaiconService } from 'app/shared/services/faicon/faicon.service';
import { NoticeService } from 'app/shared/services/notice/notice.service';
import { UiService } from 'app/shared/services/ui/ui.service';
import { SubscriptionBaseComponent } from 'app/shared/subscriptionBase/subscription-base';

@Component({
  selector: 'app-admin-announces',
  templateUrl: './admin-announces.component.html',
  styleUrls: ['./admin-announces.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdminAnnouncesComponent extends SubscriptionBaseComponent implements OnInit {

  form: FormGroup;
  notices: Notice[] = [];
  leftFilename: string;
  rightFilename: string;
  leftThumbImage: string;
  rightThumbImage: string;
  lFile: any;
  rFile: any;

  editorConfig: AngularEditorConfig = {
      editable: true,
      spellcheck: true,
      height: 'auto',
      minHeight: '0',
      maxHeight: 'auto',
      width: 'auto',
      minWidth: '0',
      translate: 'yes',
      enableToolbar: true,
      showToolbar: true,
      placeholder: 'Informations',
      defaultParagraphSeparator: '',
      defaultFontName: '',
      defaultFontSize: '',
      fonts: [
        { class: 'arial', name: 'Arial' },
        { class: 'times-new-roman', name: 'Times New Roman' },
        { class: 'calibri', name: 'Calibri' },
        { class: 'comic-sans-ms', name: 'Comic Sans MS' }
      ],

      uploadUrl: '',
      uploadWithCredentials: false,
      sanitize: true,
      toolbarPosition: 'top',
      toolbarHiddenButtons: [
        ['insertVideo'],
        ['fontSize']
      ]
  };

  private extensionsAllowed = ['jpg', 'png', 'jpeg'];

  constructor(
    private noticeService: NoticeService,
    private faIconService: FaiconService,
    private formBuilder: UntypedFormBuilder,
    private uiService: UiService,
    private dialog: MatDialog

  ) {
    super();
   }

  ngOnInit(): void {
    this.initForm();
    this.init();
    this.initSub();
  }

  getFaIcon(faIcon: string): string {
    return this.faIconService.getFaIcon(faIcon);
  }

  deleteFile(zone: string): void {
    if (zone === 'R') {
      this.rightFilename = null;
    } else {
      this.leftFilename = null;
    }
  }

  deleteImage(zone: string): void {
    let topBlock: any;
    if (zone === 'L') {
      topBlock = {
        'block': 'top_left',
        'content': this.form.get('zoneLeft').value,
        'thumbImage': ""
      };
    } else {
      topBlock = {
        'block': 'top_right',
        'content': this.form.get('zoneRight').value,
        'thumbImage': ""
      };
    }
    const params = {
      disableClose: true,
      data: {
        title: `Supprimer une image`,
        text: `Êtes-vous sûr de vouloir supprimer cette image ?`,
        action: 'updateTopText',
        block: topBlock
      }
    };

    const dialogRef = this.dialog.open(OpenDialogDeleteElementComponent, params);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        topBlock.block === 'top_left' ? this.form.controls['zoneLeft'].setValue(topBlock.content) : this.form.controls['zoneRight'].setValue(topBlock.content);
        this.noticeService.getTopNotices().subscribe({
          next: datas => {
            this.form.controls['zoneLeft'].setValue(datas.find(data => data.block === 'top_left')?.content);
            this.form.controls['zoneRight'].setValue(datas.find(data => data.block === 'top_right')?.content);
            this.leftThumbImage = datas.find(data => data.block === 'top_left')?.thumbImage;
            this.rightThumbImage = datas.find(data => data.block === 'top_right')?.thumbImage;
            this.leftThumbImage = this.checkThumbImageValue(this.leftThumbImage);
            this.rightThumbImage = this.checkThumbImageValue(this.rightThumbImage);
          }
        });
      }
    });
  }

  selectImage(event: any, zone: string): void {
    const file = event.target.files[0];
    const type = file.type?.split('/')[1];
    if (!this.extensionsAllowed.includes(type)) {
      this.uiService.showSnackbar(`Uniquement format jpg ou png`);
    } else {
      if (zone === 'R') {
        this.rFile = file;
        this.rightFilename = file.name;
      } else {
        this.lFile = file;
        this.leftFilename = file.name;
      }
    }
  }

  addNotice(): void {
    const params = {
      width: '450px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(OpenDialogAdminAnnounceComponent, params);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.init();
      }
    })
  }

  editNotice(notice: Notice): void {
    const params = {
      width: '450px',
      disableClose: true,
      data: notice
    };
    const dialogRef = this.dialog.open(OpenDialogAdminAnnounceComponent, params);
    dialogRef.afterClosed().subscribe(() => this.init());
  }

  saveTopText(value: string): void {
    let topBlock: any;
    if (value === 'left') {
      topBlock = {
        'block': 'top_left',
        'content': this.form.get('zoneLeft').value,
        'thumbImage': this.leftFilename ?? this.leftThumbImage
      };
    } else {
      topBlock = {
        'block': 'top_right',
        'content': this.form.get('zoneRight').value,
        'thumbImage': this.rightFilename ?? this.rightThumbImage
      };
    }
    const params = {
      disableClose: true,
      data: {
        title: `Modifier un texte`,
        text: value === 'left' ? `Êtes-vous sûr de vouloir modifier le texte de gauche ?` : `Êtes-vous sûr de vouloir modifier le texte de droite ?`,
        action: 'updateTopText',
        block: topBlock,
        file: value === 'left' ? this.lFile : this.rFile
      }
    };
    const dialogRef = this.dialog.open(OpenDialogDeleteElementComponent, params);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        topBlock.block === 'top_left' ? this.form.controls['zoneLeft'].setValue(topBlock.content) : this.form.controls['zoneRight'].setValue(topBlock.content);
        this.noticeService.getTopNotices().subscribe({
          next: datas => {
            this.form.controls['zoneLeft'].setValue(datas.find(data => data.block === 'top_left')?.content);
            this.form.controls['zoneRight'].setValue(datas.find(data => data.block === 'top_right')?.content);
            this.leftThumbImage = datas.find(data => data.block === 'top_left')?.thumbImage;
            this.rightThumbImage = datas.find(data => data.block === 'top_right')?.thumbImage;
            this.leftThumbImage = this.checkThumbImageValue(this.leftThumbImage);
            this.rightThumbImage = this.checkThumbImageValue(this.rightThumbImage);
            value === 'left' ? this.deleteFile('L') : this.deleteFile('R');
          }
        });
      }
    });
  }

  deleteNotice(notice: Notice): void {
    const params = {
      disableClose: true,
      data: {
        title: `Suppression d'une annonce `,
        text: `Êtes-vous sûr de vouloir supprimer l'annonce ${notice.title} ?`,
        id: notice.key,
        action: 'deleteNotice'
      }
    };
    const dialogRef = this.dialog.open(OpenDialogDeleteElementComponent, params);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.notices = this.notices.filter(n => n.key !== notice.key);
        this.checkZone(this.notices);
      }
    });
  }

  private checkThumbImageValue(thumbImage: string): string {
    if (thumbImage?.startsWith(`<img src=\"<img`) && thumbImage?.endsWith(`>\">`)) {
      const thumbImageFilter = thumbImage?.replace(/^<img src=\"/, "").replace(/\">$/, "");
      thumbImage = thumbImageFilter;
    }
    return thumbImage;
  }

  private checkZone(notices: Notice[]): void {
    notices.forEach(notice => {
      notice.isLeftZone = notice.blocks.includes('content_left') ? true : false;
      notice.isRightZone = notice.blocks.includes('content_right') ? true : false;
    });
  }

  private init(): void {
    this.noticeService.getNotices().subscribe({
      next: datas => {
        this.notices = datas;
        this.notices?.sort((x, y) => x.key < y.key ? 1 : -1);
        this.checkZone(this.notices);
      }
    });

    this.noticeService.getTopNotices().subscribe({
      next: datas => {
        this.form.controls['zoneLeft'].setValue(datas.find(data => data.block === 'top_left')?.content);
        this.form.controls['zoneRight'].setValue(datas.find(data => data.block === 'top_right')?.content);
        this.leftThumbImage = datas.find(data => data.block === 'top_left')?.thumbImage;
        this.rightThumbImage = datas.find(data => data.block === 'top_right')?.thumbImage;
        this.leftThumbImage = this.checkThumbImageValue(this.leftThumbImage);
        this.rightThumbImage = this.checkThumbImageValue(this.rightThumbImage);
      }
    });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      zoneLeft: [''],
      zoneRight: ['']
    });
  }

  private initSub(): void {
    this.subscription.add(
      this.noticeService.noticeUpdate$.subscribe(data => {
        if (data) {
          const index = this.notices.findIndex(n => n.key === data.key);
          this.notices[index] = data;
        }
      })
    );
  }

}
